import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Badge, Button, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  bulkApproveUndecidedAPI,
  getAccessReviewCampaignsAPI,
  getAccessReviewsAPI,
  makeReviewDecisionAPI,
  startAccessReviewCampaignAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import DataTableWrapper from '../../../Components/shared/DataTableWrapper';
import { platformUrl } from '../../../utils/mhNav';
import styles from './AccessReviews.module.scss';

export default function AccessReviews() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState('camp-q1');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState({});
  const [createOpen, setCreateOpen] = useState(false);
  const [newCamp, setNewCamp] = useState({ name: '' });

  const loadCampaigns = useCallback(async () => {
    const res = await getAccessReviewCampaignsAPI();
    setCampaigns(res.data || []);
  }, []);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getAccessReviewsAPI(campaignId);
      setRows(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  const applyDecision = useCallback(async (userId, decision) => {
    const prev = rowsRef.current.map((r) => ({ ...r }));
    setRows((rs) => rs.map((r) => (r.userId === userId ? { ...r, decision } : r)));
    try {
      await makeReviewDecisionAPI(userId, decision, '');
      toast.success('Saved');
    } catch (e) {
      setRows(prev);
      toast.error(e.message);
    }
  }, []);

  const columns = useMemo(
    () => [
      { name: 'User', selector: (r) => r.userName, sortable: true, grow: 1 },
      { name: 'Role', selector: (r) => r.role, sortable: true },
      {
        name: 'Last used',
        selector: (r) => new Date(r.lastUsed).toLocaleDateString(),
        sortable: true,
      },
      {
        name: 'Last login',
        selector: (r) => new Date(r.lastLogin).toLocaleDateString(),
        sortable: true,
      },
      { name: 'Justification', selector: (r) => r.justification || '—', grow: 1 },
      { name: 'Reviewer', selector: (r) => r.reviewer || '—' },
      {
        name: 'Decision',
        cell: (r) => (
          <div className="d-flex flex-wrap gap-1">
            <Button size="sm" variant="success" disabled={r.decision === 'approve'} onClick={() => applyDecision(r.userId, 'approve')}>
              Approve
            </Button>
            <Button size="sm" variant="danger" disabled={r.decision === 'revoke'} onClick={() => applyDecision(r.userId, 'revoke')}>
              Revoke
            </Button>
            <Button size="sm" variant="warning" disabled={r.decision === 'defer'} onClick={() => applyDecision(r.userId, 'defer')}>
              Defer
            </Button>
            {r.decision && <Badge bg="secondary">{r.decision}</Badge>}
          </div>
        ),
        ignoreRowClick: true,
        grow: 2,
      },
    ],
    [applyDecision]
  );

  const conditionalRowStyles = useMemo(
    () => [
      {
        when: (r) => r.decision == null || r.decision === '',
        style: { backgroundColor: 'rgba(255, 193, 7, 0.18)' },
      },
    ],
    []
  );

  const selectedIds = Object.keys(selected).filter((k) => selected[k]);

  if (error && !rows.length) {
    return (
      <div>
        <PageHeader title="Access reviews" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Audit', path: platformUrl('audit') }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={loadRows} />
      </div>
    );
  }

  return (
    <div className={styles.pagePad}>
      <PageHeader
        title="Access reviews"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Audit', path: platformUrl('audit') },
          { label: 'Access reviews' },
        ]}
        secondaryActions={[{ label: 'Security center', variant: 'outline-secondary', onClick: () => navigate(platformUrl('audit/security')) }]}
        primaryAction={{ label: 'Start review', onClick: () => setCreateOpen(true) }}
      />

      <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
        <Form.Label className="mb-0 small text-muted">Campaign</Form.Label>
        <Form.Select style={{ maxWidth: 280 }} value={campaignId} onChange={(e) => setCampaignId(e.target.value)}>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Form.Select>
      </div>

      {loading && !rows.length ? (
        <LoadingSkeleton rows={6} cols={4} />
      ) : (
        <DataTableWrapper
          columns={columns}
          data={rows}
          loading={false}
          keyField="id"
          showSearch={false}
          selectableRows
          onSelectedRowsChange={({ selectedRows: sr }) => {
            const m = {};
            (sr || []).forEach((r) => {
              m[r.id] = true;
            });
            setSelected(m);
          }}
          customTableProps={{ conditionalRowStyles }}
        />
      )}

      <div className={styles.bottomBar}>
        <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between">
          <span className="small text-muted">{selectedIds.length} selected</span>
          <div className="d-flex flex-wrap gap-2">
            <Button
              size="sm"
              onClick={async () => {
                try {
                  await bulkApproveUndecidedAPI(campaignId);
                  toast.success('Approved all undecided');
                  loadRows();
                } catch (e) {
                  toast.error(e.message);
                }
              }}
            >
              Approve all undecided
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => toast.success('Export started (simulated)')}>
              Export decisions
            </Button>
          </div>
        </div>
      </div>

      <Modal show={createOpen} onHide={() => setCreateOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>New campaign</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Label>Name</Form.Label>
          <Form.Control value={newCamp.name} onChange={(e) => setNewCamp({ name: e.target.value })} />
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={async () => {
              try {
                await startAccessReviewCampaignAPI(newCamp);
                toast.success('Campaign created');
                setCreateOpen(false);
                loadCampaigns();
              } catch (e) {
                toast.error(e.message);
              }
            }}
          >
            Create
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
